<script setup>
// The body of the book page (X47, X54): one area group at a time (tabs named
// by what they hold, when the club has several), the week strip (‹ Mon … Sun
// › a week at a time, the day picked), the viewer's allowance as chips (an
// alert at their limit), the full-width card holding the day grid (X91: the
// legend of the states on screen is a button in its top-left corner) with a column per playing area, and
// the lines under it. One component so the page can sit inside the site's
// chrome (signed in) or the club-branded frame (signed out) without two copies.
import BookingDayGrid from '~/components/bookings/BookingDayGrid.vue';
import BookingDayStrip from '~/components/bookings/BookingDayStrip.vue';
import BookingLegend from '~/components/bookings/BookingLegend.vue';
import {restOpenPlayNote} from '~/utils/bookingWeek';

defineProps({
  availability: {type: Object, default: null},
  /** groupTabs(): [{id, name, label, areas, bookableAreas, noun, nounPlural, slotMinutes}] */
  tabs: {type: Array, default: () => []},
  tab: {type: Object, default: null},
  /** dayGrid() */
  grid: {type: Object, default: null},
  /** dayStrip() */
  days: {type: Array, default: () => []},
  weekLabel: {type: String, default: ''},
  /** weekNav(): {prev, next, prevHint, nextHint, isCurrent} */
  nav: {type: Object, default: () => ({})},
  /** X88: the days "Jump to a date" may pick (jumpWindow()). */
  jumpMin: {type: String, default: null},
  jumpMax: {type: String, default: null},
  /** allowanceChips(): {chips: [{icon, text}], limitLine} */
  chips: {type: Object, default: () => ({chips: [], limitLine: null})},
  /** The legend entries for the states on screen. */
  legendItems: {type: Array, default: () => []},
  /** The viewer's allowance on the tab ({maxDurationMinutes, allDay}) and their per-slot price, for the drag ghost. */
  allowance: {type: Object, default: null},
  priceMinor: {type: Number, default: null},
  currency: {type: String, default: 'gbp'},
  sports: {type: Array, default: () => []},
  sportId: {type: String, default: null},
  gridLoading: {type: Boolean, default: false},
  footLine: {type: String, default: null},
  reasonLine: {type: String, default: null},
  failed: {type: String, default: null},
  /** Why the day asked for in the address isn't the one shown. */
  notice: {type: String, default: null},
  signedIn: {type: Boolean, default: false},
  visitorsAllowed: {type: Boolean, default: false},
  /** X54: why visitors can't book when a Visitors row would let them. */
  visitorsReason: {type: String, default: null},
  signInTo: {type: Object, default: null},
  selected: {type: Object, default: null},
  pastOpen: {type: Boolean, default: false},
  touch: {type: Boolean, default: false},
});
defineEmits(['pickGroup', 'pickDay', 'pickWeek', 'pickDate', 'pickSport', 'tap', 'explain', 'blocked', 'update:pastOpen']);
</script>

<template>
  <div class="bpb">
    <v-alert v-if="failed" type="error" variant="tonal" density="compact" class="mb-3">{{ failed }}</v-alert>

    <!-- Grant 2026-11-02 ("it flashes the open-play words instead of a table skeleton loader, the screen jumps"): before
         the availability is here (the server's render, the first load on the client) the page already has its shape:
         the week strip (its days pending, a skeleton bar under each) and the card with the grid's own table skeleton,
         so the server and the client agree and the day lands in place. A page that couldn't load shows its alert alone. -->
    <template v-if="availability || !failed">
      <div v-if="availability && !availability.bookingsEnabled" class="tl-card bpb-off">
        <v-icon size="30" color="charcoal" icon="mdi-weather-sunny"/>
        <div class="bpb-off-title">Open play</div>
        <div class="bpb-off-sub">{{ availability.clubName }} doesn't take bookings. Just turn up.</div>
      </div>

      <template v-else>
        <!-- The group (one slot step each, named by what it holds) and, when the club plays several, the sport -->
        <div v-if="availability && (tabs.length > 1 || sports.length > 1)" class="bpb-bar">
          <div v-if="tabs.length > 1" class="tl-seg bpb-seg" role="tablist" aria-label="Booking rules">
            <button v-for="t in tabs" :key="t.id" type="button" role="tab" class="tl-seg__opt" :class="{'tl-seg__opt--active': tab?.id === t.id}"
                    :aria-selected="tab?.id === t.id" @click="$emit('pickGroup', t.id)">{{ t.label || t.name }}</button>
          </div>
          <div v-if="sports.length > 1" class="bpb-sports">
            <button type="button" class="bpb-sport" :class="{'bpb-sport--on': !sportId}" @click="$emit('pickSport', null)">All sports</button>
            <button v-for="s in sports" :key="s.id" type="button" class="bpb-sport" :class="{'bpb-sport--on': sportId === s.id}" @click="$emit('pickSport', s.id)">{{ s.name }}</button>
          </div>
        </div>

        <!-- The week strip: page a week at a time, pick the day -->
        <booking-day-strip :days="days" :week-label="weekLabel" :nav="nav" :jump-min="jumpMin" :jump-max="jumpMax" class="bpb-strip" @pick-day="$emit('pickDay', $event)" @pick-week="$emit('pickWeek', $event)" @pick-date="$emit('pickDate', $event)" @blocked="$emit('blocked', $event)"/>

        <!-- The viewer's allowance as chips; an alert at their limit (D2 item 8) -->
        <v-alert v-if="chips.limitLine" type="warning" variant="tonal" density="compact" class="mb-3" icon="mdi-lock-outline">{{ chips.limitLine }}</v-alert>
        <div v-else-if="chips.chips.length" class="bpb-chips">
          <span v-for="c in chips.chips" :key="c.text" class="bpb-chip"><v-icon size="13" :icon="c.icon"/>{{ c.text }}</span>
        </div>

        <v-alert v-if="notice" type="info" variant="tonal" density="compact" class="mb-3">{{ notice }}</v-alert>
        <v-alert v-if="grid?.beyond" type="info" variant="tonal" density="compact" class="mb-3">{{ grid.opensAt ? 'This day opens for booking on ' + new Date(grid.opensAt).toLocaleDateString('en-GB', {weekday: 'long', day: 'numeric', month: 'long'}) : "This day is beyond your booking window" }}. You can look, but not book yet.</v-alert>
        <v-alert v-else-if="reasonLine" type="info" variant="tonal" density="compact" class="mb-3">{{ reasonLine }}</v-alert>
        <v-alert v-else-if="availability && !signedIn && !visitorsAllowed" type="info" variant="tonal" density="compact" class="mb-3">
          <nuxt-link :to="signInTo" class="bpb-link">Sign in</nuxt-link> to book. {{ visitorsReason || "Visitors can't book here yet." }}
        </v-alert>

        <!-- The card: the day grid. X88: no hours line; the grid shows the hours and a changed day is marked on the strip.
             X91: the legend (only the states on screen) is a button in the grid's top-left corner that opens its dialog. -->
        <!-- X202 (R82 L12): a closed morning with the rest open play says so (the grid can't draw unpainted time) -->
        <div v-if="restOpenPlayNote(grid?.note)" class="bpb-note"><v-icon size="15" icon="mdi-information-outline"/>{{ grid.note }}</div>
        <div class="tl-card bpb-card">
          <booking-day-grid :grid="availability ? grid : null" :selected="selected" :loading="gridLoading || !availability" :allowance="allowance" :price-minor="priceMinor" :currency="currency" :past-open="pastOpen" :touch="touch" :noun-plural="tab?.nounPlural"
                            @tap="$emit('tap', $event)" @explain="$emit('explain', $event)" @update:past-open="$emit('update:pastOpen', $event)">
            <template #corner><booking-legend v-if="legendItems.length" :items="legendItems"/></template>
          </booking-day-grid>
        </div>
        <!-- Grant 2026-11-02: the tab's own noun ("A shared court", "A shared court or board") -->
        <div v-if="availability && sports.length > 1 && sportId" class="bpb-foot">A shared {{ tab?.noun || 'playing area' }} is one space: any booking blocks it for every sport.</div>
        <div v-if="availability && footLine" class="bpb-foot">{{ footLine }}</div>
      </template>
    </template>
  </div>
</template>

<style scoped lang="scss">
.bpb-off { text-align: center; padding: 30px 20px; }
.bpb-off-title { font-size: 15px; font-weight: 700; margin-top: 6px; }
.bpb-off-sub { font-size: 13px; color: rgb(var(--v-theme-textSubtle)); margin-top: 3px; }
/* X202 (R82 L13: CLS 0.95 at 390, the footer pushed down by 570px when the week arrived) and Grant 2026-11-02: while the
   availability loads (SSR'd, it arrives on mount) the strip and the card are already on the page, the card holding the
   grid's table skeleton (BookingDayGrid), so the footer starts below the fold and nothing in view moves. */

.bpb-bar { display: flex; flex-wrap: wrap; align-items: center; gap: 10px 16px; margin: 14px 0 14px; }
.bpb-seg { flex: 0 1 auto; overflow-x: auto; -webkit-overflow-scrolling: touch; }
.bpb-strip { margin-bottom: 12px; }
/* X202 (R82 L12): the day's open-play line over the grid, quiet like the foot lines */
.bpb-note { display: flex; align-items: center; gap: 6px; margin: 0 0 8px; font-size: 12.5px; color: rgb(var(--v-theme-textSubtle)); .v-icon { flex: 0 0 auto; } }

.bpb-sports { display: flex; flex-wrap: wrap; gap: 6px; }
.bpb-sport {
  height: 30px; padding: 0 12px; border-radius: 999px; border: 1px solid rgb(var(--v-theme-borderLight)); background: rgb(var(--v-theme-surface));
  font-size: 12px; font-weight: 600; font-family: inherit; cursor: pointer; white-space: nowrap; color: rgb(var(--v-theme-text));
  &:hover { background: rgb(var(--v-theme-light)); }
  &--on { background: rgb(var(--v-theme-brandTint)) !important; color: rgb(var(--v-theme-primary)); border-color: rgb(var(--v-theme-primary)); }
}

.bpb-chips { display: flex; flex-wrap: wrap; gap: 6px; margin: 0 0 12px; }
.bpb-chip {
  display: inline-flex; align-items: center; gap: 5px; height: 26px; padding: 0 10px 0 8px; border-radius: 999px;
  background: rgb(var(--v-theme-light)); color: rgb(var(--v-theme-charcoal)); font-size: 11.5px; font-weight: 600; white-space: nowrap;
  .v-icon { color: rgb(var(--v-theme-textSubtle)); }
}

/* X59: clip (not hidden) keeps the rounded corners without making the card a scroll container, so the grid's header row
   can stick to the page top; the grid is its full height and only scrolls sideways inside. */
.bpb-card { padding: 0; overflow: clip; width: 100%; }
.bpb-foot { font-size: 12px; color: rgb(var(--v-theme-textSubtle)); line-height: 1.5; margin: 8px 4px 0; }
.bpb-link { color: rgb(var(--v-theme-primary)); font-weight: 600; }

@media (max-width: 640px) {
  .bpb-bar { gap: 8px 10px; margin: 10px 0 12px; }
  .bpb-seg { max-width: 100%; .tl-seg__opt { font-size: 12px; padding: 7px 10px; } }
}
</style>
